import React, {useCallback, useEffect, useState} from 'react'
import {convertToHours, formatDateToDDMMYYYY} from '../../common-function/formatDateToYearMonth'
import {restRequest} from '../../../api-utils/apiQueries'
import {KTIcon} from '../../../_metronic/helpers'
import WalletFilter from './WalletFilter'
import {useNavigate} from 'react-router-dom'
import Pagination from '../../common-function/Pagination'
import {ExportReportPayload, useExportReport} from '../../common-function/downloadExportFile'
import {
  appendDateFilterToBody,
  appendDateFilterToExport,
} from '../../common-function/adminDateFilterPayload'
import {useAdminListState} from '../../common-function/useAdminListState'
import ListFilterToolbar from '../../common-function/ListFilterToolbar'
import ListPageHeader from '../../common-function/ListPageHeader'
import {
  buildWalletFilterSummary,
  getDefaultWalletFilter,
  WalletFilterState,
} from './walletDateRange'

const LIST_STORAGE_KEY = 'wallet-management'

const resolveWalletUserName = (item: any): string =>
  item?.user?.name ?? item?.user_name ?? '—'

const resolveWalletAmount = (item: any): string => {
  if (item?.amount) return item.amount.replace(/^\$/, '')
  const points = Number(item?.points || 0)
  return (points / 100).toFixed(2)
}

/** Prefer API `has_trip`; fall back for older responses. */
const walletRowHasTrip = (item: any): boolean =>
  item?.has_trip === true || Number(item?.trip_id) > 0

const resolveWalletTripId = (item: any): number | null => {
  const id = Number(item?.trip_id)
  return Number.isFinite(id) && id > 0 ? id : null
}

const WalletWrapper = () => {
  const {runExport, exporting} = useExportReport()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [wallets, setWallets] = useState<any>([])
  const [showFilter, setShowFilter] = useState<boolean>(false)
  const [totalPages, setTotalPages] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)
  const navigate = useNavigate()

  const {
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    activeFilter,
    applyFilter,
    resetToDefault,
    resetPageForSearch,
    isFilterActive,
  } = useAdminListState<WalletFilterState>({
    storageKey: LIST_STORAGE_KEY,
    getDefaultFilter: getDefaultWalletFilter,
    isDefaultFilter: (f, search) =>
      f.preset === 'today' && f.transaction_type === 'all' && !search.trim(),
  })

  const fetchWalletData = useCallback(
    async (query: string) => {
      setIsLoading(true)
      const bodyData: Record<string, unknown> = {
        transaction_type: activeFilter.transaction_type,
        page,
        search: query,
      }
      appendDateFilterToBody(bodyData, activeFilter)

      try {
        const response: any = await restRequest.getWalletsData(bodyData)
        const data = response.data.data
        setWallets(data.data ?? [])
        setTotalPages(data.pager?.totalPages ?? 0)
        setTotalRecords(data.pager?.totalRecords ?? 0)
      } catch (error) {
        console.error('Failed to load wallet data:', error)
        setWallets([])
        setTotalPages(0)
        setTotalRecords(0)
      } finally {
        setIsLoading(false)
      }
    },
    [page, activeFilter]
  )

  useEffect(() => {
    fetchWalletData(searchQuery)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeFilter])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (e.target.value === '') {
      resetPageForSearch()
      fetchWalletData('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      resetPageForSearch()
      fetchWalletData(searchQuery)
    }
  }

  const submitSearch = () => {
    resetPageForSearch()
    fetchWalletData(searchQuery)
  }

  const handleExportWallet = () => {
    const payload: ExportReportPayload = {
      type: 'wallet',
      transaction_type: activeFilter.transaction_type,
    }
    appendDateFilterToExport(payload, activeFilter)
    if (searchQuery?.trim()) payload.search = searchQuery.trim()
    runExport(payload)
  }

  const filterSummary = buildWalletFilterSummary(activeFilter, searchQuery, totalRecords)

  return (
    <div>
      <p className='card-label fw-bold fs-3 mb-2'>Wallet Management</p>

      <ListFilterToolbar
        filterSummary={filterSummary}
        showReset={isFilterActive}
        onReset={resetToDefault}
      />

      <div className='card'>
        <ListPageHeader
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onSearchKeyDown={handleKeyDown}
          onSearchSubmit={submitSearch}
          searchPlaceholder='Search wallet by user name'
          isLoading={isLoading}
          isFilterActive={isFilterActive}
          onOpenFilter={() => setShowFilter(true)}
          onExport={handleExportWallet}
          exporting={exporting}
        />
        <div className='card-body py-3'>
          {isLoading ? (
            <div className='d-flex justify-content-center py-10'>
              <div className='spinner-border text-primary'></div>
            </div>
          ) : (
            <div className='table-responsive'>
              {wallets.length > 0 ? (
                <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='min-w-150px'>User Name</th>
                      <th className='min-w-140px'>Transaction Type</th>
                      <th className='min-w-120px'>Amount</th>
                      <th className='min-w-120px'>Payment Mode</th>
                      <th className='min-w-120px'>Trip</th>
                      <th className='min-w-120px'>Time Of Transaction</th>
                      <th className='min-w-120px'>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wallets.map((item: any, index: number) => (
                      <tr key={item.id ?? index}>
                        <td>
                          <p className='text-dark fw-bold text-hover-primary fs-6'>
                            {resolveWalletUserName(item)}
                          </p>
                        </td>
                        <td>
                          <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                            {item.transaction_type ?? '—'}
                          </p>
                        </td>
                        <td>
                          <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                            ${resolveWalletAmount(item)}
                          </p>
                        </td>
                        <td>
                          <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                            {item.payment_mode ?? '—'}
                          </p>
                        </td>
                        <td>
                          {walletRowHasTrip(item) ? (
                            <button
                              type='button'
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                              title='View trip details'
                              onClick={() => {
                                const tripId = resolveWalletTripId(item)
                                if (tripId) navigate(`/trip-management/${tripId}`)
                              }}
                            >
                              <KTIcon iconName='eye' className='fs-1' />
                            </button>
                          ) : (
                            <span className='text-muted fs-7'>—</span>
                          )}
                        </td>
                        <td className='text-dark fw-bold text-hover-primary fs-6'>
                          {convertToHours(new Date(item.createdAt))}
                        </td>
                        <td>
                          <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                            {formatDateToDDMMYYYY(item.createdAt)}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className='fs-5 pt-3 text-center'>No wallet transactions for this filter.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination totalPages={totalPages} page={page} setPage={setPage} />
      )}

      {showFilter && (
        <WalletFilter
          showFilter={showFilter}
          toggle={() => setShowFilter(false)}
          activeFilter={activeFilter}
          onApply={applyFilter}
        />
      )}
    </div>
  )
}

export default WalletWrapper
